import { getMeetingByID } from '@/services/meeting'
import { produce } from 'immer'
import { StateCreator } from 'zustand'
import useStore, { IStore } from './store'
import {
  extractActiveHearingLAR,
  extractActiveLAR,
  extractActiveOOR,
  extractCredentialIssuanceStep,
  extractCurrentLARAuthCount,
  extractIHeard,
  extractJitsiMessage,
  extractJoinMultisigMember,
  extractLARChallengeMessages,
  extractLARVotes,
  extractLeadQAR,
  extractLEMultisigAidInitiator,
  extractMultisigMemberSignal,
  extractMultiSigSigner,
  extractOORAuth,
  extractQARCeremonyStep,
  extractQARChallengeMessages,
  extractRoleCredRecipients,
  extractSecondaryQARApproval,
  groupDevicesByKind,
  prepareActiveLAR,
  prepareActiveOOR,
  prepareCurrentLARAuthCount,
  prepareQARCeremonySteps,
  uniqChallengeString
} from '@/utils/ceremony'
import {
  CEREMONY_TYPE,
  FETCHING_STATUS,
  PHOTO_LOADING_STATUS
} from '@/config/constants'
import { GroupAid } from './signify'
import { IssueCredentialResult } from 'signify-ts'
import { cloneDeep } from 'lodash'
import {
  JitsiMessageCreator,
  JitsiMessagesEnum,
  MessagePayloadTypes
} from '@/state/models'
import type { PossibleOorRecipientDto } from '@/api/origin-workflow-svc'
import { ceremonyTypeFromURL } from '@/utils/common'
import { isLAR } from '@/utils/auth'
import { isRecipient } from '@/features/oor'
import {
  captureErrorWithScope,
  SentryContextEnum
} from '@/shared/error-capture'
import { GetMeetingResponseDto } from '@/api/origin-webmeet-svc'

export enum JITSI_CONNECTION_STATUS {
  NOT_CONNECTED,
  CONNECTED,
  JOINED,
  DISCONNECTED
}

interface IIdVersePhotoAvailability {
  available: boolean
  url?: string | null
  fetchingStatus?: FETCHING_STATUS
  photoLoadingStatus: PHOTO_LOADING_STATUS
}

export type Identifiers = {
  name: string
  prefix: string
  oobi: string[]
  participantId?: string
  isMultisig: boolean
  group?: GroupAid
  type?: string
}

export type Participant = {
  id: string
  jitsiUserId: string
  name: string
  orgId: string
  orgName: string
  verified: boolean
  photo: string
  muted: boolean
  videoOff: boolean
  role: string[]
  identifiers: Identifiers[]
  idVersePhotoAvailable?: IIdVersePhotoAvailability
  joinedMeetingAt?: number
}

export enum TriggerSignify {
  WAITING = 'WAITING',
  ACCEPT = 'ACCEPT',
  COMPLETE = 'COMPLETE',
  FAILED = 'FAILED'
}

export type ChallengeString = {
  from: string
  to: string
  challengeString: string
  triggerSignify: TriggerSignify
  completed: boolean
}

export type AIDVerificationString = {
  from: { participantId: string; completionStatus: boolean }[]
  to: string
  triggerSignify: TriggerSignify
  completed: boolean
  messageId: string
  joinedMeetingAt: number
}

export enum QAR_CEREMONY_STEP {
  PHOTO_VERIFY,
  START_CEREMONY,
  AID_VERIFICATION,
  QAR_CHALLENGE,
  QAR_CHALLENGE_OOR,
  QAR_OOR_CEREMONY_STARTED,
  QAR_CHALLENGE_STARTED,
  LAR_CHALLENGE,
  LAR_CHALLENGE_STARTED,
  OOR_AUTH_PRE,
  OOR_AUTH_STARTED,
  OOR_AUTH_FINAL,
  MULTI_SIG_STARTED,
  COMPLETED,
  END,
  ERROR
}

export enum CredentialIssuanceStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETE = 'COMPLETE',
  FAILED = 'FAILED'
}

export interface ILARVote {
  id: string
  accepted: boolean
}

export interface IOORAuth {
  id: string
  votes: any[]
  qarApproval: string[]
}

export interface RoleCredentialRecipient {
  userId: string
  approvalVotes: string[]
  recipientAid: string
  issuerAid: string
  authCredentialRecipientAid: string
  schema: string
  credentialSaid?: string
  credentialAttributes?: any
  issuerMembers?: CredentialIssuanceMember[]
  status: CredentialIssuanceStatus
  error?: any
}

export interface CredentialIssuanceMember {
  // OOR(t2), Q1, Q2, L1,, R1
  issuedBy: string
  aid: string
  isMaker: boolean
  issuerTo: string
  said: string
  hasJoinedCreateCredential?: boolean
  hasJoinedGrantCredential?: boolean
  hasJoinedAdmitCredential?: boolean
}

/**
 * Interface representing the state of a Legal Entity (LE) vLEI credential issuance.
 *
 * @interface LECredentialIssuanceState
 * @property {string} schema - Schema SAID of LE credential
 * @property {string} credentialSaid - SAID of LE credential
 * @property {string} recipientAid - Recipient AID (LE group AID)
 * @property {string} issuerAid - Issuer AID (QVI group AID)
 * @property {CredentialIssuanceMember[]} [issuerMembers] - array of members associated with the issuer aid
 */
export interface LECredentialIssuanceState {
  schema: string
  credentialSaid: string
  recipientAid: string
  issuerAid: string
  issuerMembers?: CredentialIssuanceMember[]
}

export interface IMultisigAid {
  groupAlias: string
  members?: IMultisigMember[]
}

export interface IMultisigMember {
  userId: string
  localAid: string
  localAidName: string
  isMaker: boolean
  hasJoinedIcp?: boolean
  hasJoinedEndRoleAuth?: boolean
  hasJoinedRegistry?: boolean
  hasCompletedIcpOp?: boolean
  agents?: IMultisigAidAgent[]
}

export interface IMultisigAidAgent {
  eid: string
  isAuthorized: boolean
}

export interface ICreateCredentialResult {
  roleRecipientId: string
  result: IssueCredentialResult
}

export interface IMultisigMemberSignal {
  sender: string
  target?: string
  targetType?: string
  msgType: MULTISIG_AID_MESSAGE_TYPE
  // `groupAid` is nullable: at the start of a multisig inception flow the
  // group AID doesn't exist yet (LARMultisig passes `null` for the JOIN /
  // MEMBER_JOINED signals before the AID is created). Once the group AID
  // is incepted, subsequent signals carry the real string value.
  groupAid: string | null
  groupAidName: string
  data: any
}

export enum MULTISIG_AID_MESSAGE_TYPE {
  START_MULTISIG_AID_INCEPTION = 'START_MULTISIG_AID_INCEPTION',
  JOIN_MULTISIG_AID_INCEPTION = 'JOIN_MULTISIG_AID_INCEPTION',
  MEMBER_JOINED_MULTISIG_AID_INCEPTION = 'MEMBER_JOINED_MULTISIG_AID_INCEPTION',
  JOIN_REGISTRY_INCEPTION = 'JOIN_REGISTRY_INCEPTION',
  JOIN_END_ROLE_AUTH = 'JOIN_END_ROLE_AUTH',
  MEMBER_JOINED_END_ROLE_AUTH = 'MEMBER_JOINED_END_ROLE_AUTH',
  MEMBER_JOINED_REGISTRY_INCEPTION = 'MEMBER_JOINED_REGISTRY_INCEPTION',
  LE_AID_INCEPTION_COMPLETE = 'LE_AID_INCEPTION_COMPLETE',
  JOIN_CREATE_CREDENTIAL = 'JOIN_CREATE_CREDENTIAL',
  MEMBER_JOINED_CREATE_CREDENTIAL = 'MEMBER_JOINED_CREATE_CREDENTIAL',
  JOIN_GRANT_CREDENTIAL = 'JOIN_GRANT_CREDENTIAL',
  MEMBER_JOINED_GRANT_CREDENTIAL = 'MEMBER_JOINED_GRANT_CREDENTIAL',
  JOIN_ADMIT_CREDENTIAL = 'JOIN_ADMIT_CREDENTIAL',
  MEMBER_JOINED_ADMIT_CREDENTIAL = 'MEMBER_JOINED_ADMIT_CREDENTIAL',
  LE_CREDENTIAL_ISSUE_COMPLETE = 'LE_CREDENTIAL_ISSUE_COMPLETE',
  AUTH_CREDENTIAL_ISSUE_COMPLETE = 'AUTH_CREDENTIAL_ISSUE_COMPLETE',
  ROLE_CREDENTIAL_ISSUE_COMPLETE = 'ROLE_CREDENTIAL_ISSUE_COMPLETE',
  ROLE_CREDENTIAL_RECEIVED = 'ROLE_CREDENTIAL_RECEIVED',
  WAIT_KERIA_OPERATION_COMPLETION = 'WAIT_KERIA_OPERATION_COMPLETION',
  OPERATION_COMPLETED = 'OPERATION_COMPLETED',
  ALL_AUTH_CREDENTIALS_ISSUANCE_COMPLETE = 'ALL_AUTH_CREDENTIALS_ISSUANCE_COMPLETE',
  LE_ADMIT_CREDENTIAL_COMPLETE = 'LE_ADMIT_CREDENTIAL_COMPLETE',
  SELF_SIMULATE_JOINED_CREATE_CREDENTIAL = 'SELF_SIMULATE_JOINED_CREATE_CREDENTIAL',
  SELF_SIMULATE_JOINED_GRANT_CREDENTIAL = 'SELF_SIMULATE_JOINED_GRANT_CREDENTIAL'
}

export type CreateLEMultisigAidMessagePayload = {
  msgType: string
  initiatorId: string
  alias: string
  members: string[]
}

export interface ICredentialIssuanceStep {
  messageId: string
  params?: { [key: string]: string }
}

export interface ILeadQAR {
  leadQAR: string
}

export interface ISecondaryQARApproval {
  secondaryQAR: string
}

export interface CredentialSaid {
  receiver?: string
  said: string
  type: string
}

export interface ICredentialIssuanceStatusChange {
  status: CredentialIssuanceStatus
}

export interface LEvLEIIssuanceError {
  type: SentryContextEnum
  message: string
}

export type IMeetStore = {
  meetingDetails: {
    details: GetMeetingResponseDto
    fetchingStatus: FETCHING_STATUS
  }
  conferenceStatus: JITSI_CONNECTION_STATUS
  devices: {
    audioInput: MediaDeviceInfo[]
    audioOutput: MediaDeviceInfo[]
    videoInput: MediaDeviceInfo[]
  }
  ceremonySteps: {
    credentialIssuanceStatus: CredentialIssuanceStatus
    credentialIssuanceStatusError?: LEvLEIIssuanceError
    credentialSaid: CredentialSaid[]
    leadQAR: string | null
    activeLAR: string | null
    activeHearingLAR: string | null
    activeOOR: string | null
    qarCurrentStep: QAR_CEREMONY_STEP
    participants: Participant[]
    Recipients: PossibleOorRecipientDto[]
    currentLARAuthCount: number
    AIDVerification: {
      AIDTodos: AIDVerificationString[]
      stepCompleted: boolean
      AIDTodosInProgress: boolean
    }
    QARChallenge: {
      ChallengeStrings: ChallengeString[]
      completed: boolean
    }
    LARChallenge: {
      LARChallenges: ChallengeString[]
      completed: boolean
    }
    OORAuth: {
      recipients: IOORAuth[]
      completed: boolean
      roleCredentialRecipients: RoleCredentialRecipient[]
    }
    multiSig: {
      selectedSigner: number
      LARVotes: ILARVote[]
      leMultisigAidInceptPayload: CreateLEMultisigAidMessagePayload | null
      joinLeMultisigAidPayload: CreateLEMultisigAidMessagePayload | null
      multisigMemberSignal: IMultisigMemberSignal | null
    }
    eventLogs: string[]
    credentialIssuanceStep: ICredentialIssuanceStep | null
  }
  setRoom: (room: any) => void
  setJitsiConnection: (connection: any) => void
  addParticipants: (participant: Participant) => void
  removeParticipant: (id: string) => void
  updateParticipant: (participant: Participant) => void
  updateDevices: (devices: MediaDeviceInfo[]) => void
  setConferenceStatus: (status: JITSI_CONNECTION_STATUS) => void
  getMeetingDetails: (meetingId: string) => void
  setQARCurrentStep: (step: QAR_CEREMONY_STEP) => void
  sendMessage: (msg: string | object) => void
  activateLARLocally: (
    participantID: string,
    options?: { isOOR?: boolean }
  ) => void
  sendErrorMessageToAllParticipantsAndNotifySentry: (data: {
    sentryContext: SentryContextEnum
    sentryContextPayload?: any
    message?: string
    error?: any
  }) => void
  endCeremony: () => void
  setLEMultisigInceptPayload: (
    payload: CreateLEMultisigAidMessagePayload
  ) => void
  logEvents: (eventMessages: string[]) => void
  setRecipient: (recipients: PossibleOorRecipientDto[]) => void
  setCurrentLARAuthCount: (currentLARAuthCount: number) => void
  initMeetSubscribers: (
    subscribe?: typeof useStore.subscribe,
    getState?: typeof useStore.getState,
    setState?: typeof useStore.setState
  ) => () => void
  generateLARsVerificationsForAID: () => void
  iSaidItForAID: (todo: AIDVerificationString) => void
  iHeardItForAID: (todo: AIDVerificationString) => void
  checkAndActivateLARForAID: (
    todos: (AIDVerificationString | ChallengeString)[]
  ) => void
  resetMeetStore: () => void
}

/**
 * A stable per-store holder for a live Jitsi object (the conference room or the
 * connection).
 *
 * t-6 / B1: the live `JitsiConference` is cyclic and non-serialisable. If it
 * were stored as an ordinary slice value via `set({ _room })`, then in the dev
 * profile the `devtools` middleware (store.ts:32-34) would serialise it to the
 * Redux DevTools bridge on every `set`, and any broad `subscribeWithSelector`
 * subscriber would wake on each `setRoom`. To avoid both, the room/connection
 * are NOT carried through `set` at all and are never placed on slice state:
 * each store gets ONE `RoomBox` created in the slice creator's closure, and
 * `setRoom`/`setJitsiConnection` mutate `box.current` IN PLACE. The four send
 * sites and `endCeremony` read the closure variable directly (`_roomBox.current`),
 * NOT `get()`. Because the box never enters slice state, `immer`/`produce` never
 * freezes it, `devtools` never serialises it, and `subscribeWithSelector`
 * subscribers never wake on a room write. `current` starts `undefined`,
 * preserving the CER-3 "throw before setRoom" contract
 * (`_roomBox.current.sendMessage(...)` throws TypeError while undefined).
 */
interface RoomBox {
  current: any
}

const initialState = {
  conferenceStatus: JITSI_CONNECTION_STATUS.NOT_CONNECTED,
  ceremonySteps: {
    credentialIssuanceStatus: CredentialIssuanceStatus.NOT_STARTED,
    credentialSaid: [],
    leadQAR: null,
    activeLAR: null,
    activeHearingLAR: null,
    activeOOR: null,
    currentLARAuthCount: 0,
    participants: [],
    Recipients: [],
    qarCurrentStep: QAR_CEREMONY_STEP.PHOTO_VERIFY,
    AIDVerification: {
      AIDTodos: [],
      stepCompleted: false,
      AIDTodosInProgress: false
    },
    QARChallenge: {
      ChallengeStrings: [],
      completed: false
    },
    LARChallenge: {
      LARChallenges: [],
      completed: false
    },
    OORAuth: {
      recipients: [],
      completed: false,
      roleCredentialRecipients: []
    },
    multiSig: {
      selectedSigner: 0,
      LARVotes: [],
      leMultisigAidInceptPayload: null,
      joinLeMultisigAidPayload: null,
      multisigMemberSignal: null
    },
    eventLogs: [],
    credentialIssuanceStep: null
  },
  devices: {
    audioInput: [],
    audioOutput: [],
    videoInput: []
  },
  meetingDetails: {
    details: {} as GetMeetingResponseDto,
    fetchingStatus: FETCHING_STATUS.NOT_INITIATED
  }
}
const meetStore: StateCreator<IStore, [], [], IMeetStore> = (set, get) => {
  // One stable holder per store instance (see RoomBox doc). Created in the
  // creator CLOSURE — NOT in `initialState` and NOT placed on the returned
  // slice state — so two stores never share a box, a stale room cannot leak
  // across store instances, and (critically, B1) the box is never reachable
  // through the state object that `immer`/`produce` freezes or that `devtools`
  // serialises and `subscribeWithSelector` observes. The four send sites and
  // `endCeremony` read these closure variables directly; nothing reads them
  // off `get()`, so they are encapsulated by construction (t-6) — they cannot
  // appear on the public `IMeetStore` surface at all.
  const _roomBox: RoomBox = { current: undefined }
  const _connectionBox: RoomBox = { current: undefined }

  return {
    ...initialState,
    setRoom: (room) => {
      // t-6 / B1: store the live room in the per-store closure box. NO `set(...)`
      // for the room, so it never reaches devtools serialisation, immer freezing,
      // or `subscribeWithSelector` subscribers (see RoomBox doc).
      //
      // KNOWN-INCOMPLETE DI (t-6 caveat): this slice-routed room is the canonical
      // outbound path, but it is NOT the only one. `LARMultisig.tsx:793-794` sends
      // live LE-multisig JOIN signals through `window.room?.sendMessage(msg)` (the
      // slice `sendMessage` is commented out directly above it), and
      // `Meet.tsx:678/700` use `window.room` for debug. Those bypass this box
      // entirely and are deliberately NOT reconciled here — a real residual the
      // ceremony harness bus never exercises.
      _roomBox.current = room

      room.on(
        JitsiMeetJS.events.conference.MESSAGE_RECEIVED,
        (_id: any, message: any) => {
          let payload: any
          const extractedJitsiMsg = extractJitsiMessage(message)
          // "{"QARCeremonySteps":3}"
          // console.log('============MESSAGE_RECEIVED', message)

          // TECH_DEBT: remove_legacy_payload_extraction
          if (extractedJitsiMsg) {
            switch (extractedJitsiMsg.generalJitsiMessage) {
              case JitsiMessagesEnum.AID_VERIFICATION_GENERATE_LARS_TODOS:
                payload =
                  extractedJitsiMsg.generalJitsiPayload as MessagePayloadTypes[JitsiMessagesEnum.AID_VERIFICATION_GENERATE_LARS_TODOS]

                set(
                  produce((state: IMeetStore) => {
                    state.ceremonySteps.AIDVerification.AIDTodos = payload.sort(
                      (
                        a: { joinedMeetingAt: number },
                        b: { joinedMeetingAt: number }
                      ) => a.joinedMeetingAt - b.joinedMeetingAt
                    )
                    // console.log('AIDTodos', payload)
                  })
                )
                break
              case JitsiMessagesEnum.AID_VERIFICATION_I_Heard_It:
              case JitsiMessagesEnum.AID_VERIFICATION_I_Said_It:
                payload = extractedJitsiMsg.generalJitsiPayload as
                  | MessagePayloadTypes[JitsiMessagesEnum.AID_VERIFICATION_I_Heard_It]
                  | MessagePayloadTypes[JitsiMessagesEnum.AID_VERIFICATION_I_Said_It]

                set(
                  produce((state: IMeetStore) => {
                    let index =
                      state.ceremonySteps.AIDVerification.AIDTodos.findIndex(
                        (todo) => todo.messageId === payload.messageId
                      )
                    if (index !== -1) {
                      state.ceremonySteps.AIDVerification.AIDTodos[index] =
                        payload
                    } else {
                      // console.log('message not found on jitsi')
                    }
                  })
                )
                break
              case JitsiMessagesEnum.SET_CREDENTIAL_SAID:
                payload =
                  extractedJitsiMsg.generalJitsiPayload as MessagePayloadTypes[JitsiMessagesEnum.SET_CREDENTIAL_SAID]
                set(
                  produce((state: IMeetStore) => {
                    state.ceremonySteps.credentialSaid.push(payload)
                  })
                )
                break
              case JitsiMessagesEnum.CREDENTIAL_ISSUANCE_STATUS_CHANGE:
                payload =
                  extractedJitsiMsg.generalJitsiPayload as MessagePayloadTypes[JitsiMessagesEnum.CREDENTIAL_ISSUANCE_STATUS_CHANGE]
                set(
                  produce((state: IMeetStore) => {
                    state.ceremonySteps.credentialIssuanceStatus =
                      payload.status
                  })
                )
                break
              case JitsiMessagesEnum.LEvLEI_ISSUANCE_ERROR:
                payload =
                  extractedJitsiMsg.generalJitsiPayload as MessagePayloadTypes[JitsiMessagesEnum.LEvLEI_ISSUANCE_ERROR]
                set(
                  produce((state: IMeetStore) => {
                    const { setQARCurrentStep } = get()
                    state.ceremonySteps.credentialIssuanceStatusError = payload
                    setQARCurrentStep(QAR_CEREMONY_STEP.ERROR)
                  })
                )
                break
            }
          }

          const currentStep = extractQARCeremonyStep(message)
          if (currentStep) {
            set(
              produce((state: IMeetStore) => {
                state.ceremonySteps.qarCurrentStep = currentStep
              })
            )
          }

          const QARChallengeMessage = extractQARChallengeMessages(message)
          if (QARChallengeMessage) {
            set(
              produce((state: IMeetStore) => {
                state.ceremonySteps.QARChallenge.ChallengeStrings =
                  uniqChallengeString(
                    QARChallengeMessage,
                    get().ceremonySteps.QARChallenge.ChallengeStrings
                  )
              })
            )
          }

          const LARChallengeMessage = extractLARChallengeMessages(message)
          if (LARChallengeMessage) {
            set(
              produce((state: IMeetStore) => {
                state.ceremonySteps.LARChallenge.LARChallenges =
                  uniqChallengeString(
                    LARChallengeMessage,
                    get().ceremonySteps.LARChallenge.LARChallenges
                  )
              })
            )
          }

          const IHeard = extractIHeard(message)
          if (IHeard) {
            set(
              produce((state: IMeetStore) => {
                let index =
                  state.ceremonySteps.QARChallenge.ChallengeStrings.findIndex(
                    (lc) => lc.from === IHeard.from && lc.to === IHeard.to
                  )
                if (index > -1) {
                  state.ceremonySteps.QARChallenge.ChallengeStrings[index] =
                    IHeard
                } else {
                  index =
                    state.ceremonySteps.LARChallenge.LARChallenges.findIndex(
                      (lc) => lc.from === IHeard.from && lc.to === IHeard.to
                    )
                  // Guard the fallback assignment: an IHeard matching neither
                  // ChallengeStrings nor LARChallenges yields index === -1.
                  // Writing `LARChallenges[-1] = IHeard` would smuggle a
                  // non-index property onto the array and corrupt its shape, so
                  // ignore an unknown message (matches the QARChallenge branch's
                  // `index > -1` semantics). See BUG-0043.
                  if (index > -1) {
                    state.ceremonySteps.LARChallenge.LARChallenges[index] =
                      IHeard
                  }
                }
              })
            )
          }

          const activeLAR = extractActiveLAR(message)
          if (activeLAR) {
            set(
              produce((state: IMeetStore) => {
                state.ceremonySteps.activeLAR = activeLAR
              })
            )
          }
          const activeHearingLAR = extractActiveHearingLAR(message)
          if (activeHearingLAR) {
            set(
              produce((state: IMeetStore) => {
                state.ceremonySteps.activeHearingLAR = activeHearingLAR
              })
            )
          }

          const activeOOR = extractActiveOOR(message)
          if (activeOOR) {
            set(
              produce((state: IMeetStore) => {
                state.ceremonySteps.activeOOR = activeOOR
              })
            )
          }

          const selectedSigner = extractMultiSigSigner(message)
          if (selectedSigner) {
            set(
              produce((state: IMeetStore) => {
                state.ceremonySteps.multiSig.selectedSigner = selectedSigner
              })
            )
          }

          const LARVote = extractLARVotes(message)
          if (LARVote) {
            set(
              produce((state: IMeetStore) => {
                const existingLARVote =
                  state.ceremonySteps.multiSig.LARVotes.findIndex(
                    (lv) => lv.id === LARVote.id
                  )
                if (existingLARVote > -1)
                  state.ceremonySteps.multiSig.LARVotes[existingLARVote] =
                    LARVote
                else state.ceremonySteps.multiSig.LARVotes.push(LARVote)
              })
            )
          }

          const leVleiMultisigInitiatorPayload =
            extractLEMultisigAidInitiator(message)
          if (leVleiMultisigInitiatorPayload) {
            set(
              produce((state: IMeetStore) => {
                state.ceremonySteps.multiSig.leMultisigAidInceptPayload =
                  leVleiMultisigInitiatorPayload
              })
            )
          }

          const joinLeMultisigAidPayload = extractJoinMultisigMember(message)
          if (joinLeMultisigAidPayload) {
            set(
              produce((state: IMeetStore) => {
                state.ceremonySteps.multiSig.joinLeMultisigAidPayload =
                  joinLeMultisigAidPayload
              })
            )
          }

          const OORAuth = extractOORAuth(message)
          if (OORAuth) {
            set(
              produce((state: IMeetStore) => {
                const existingOORAuth =
                  state.ceremonySteps.OORAuth.recipients.findIndex(
                    (c) => c.id === OORAuth.id
                  )
                if (existingOORAuth > -1) {
                  state.ceremonySteps.OORAuth.recipients[existingOORAuth] =
                    OORAuth
                } else state.ceremonySteps.OORAuth.recipients.push(OORAuth)
              })
            )
          }

          // const OORAuthCan = extractRoleAuthCandidate(message)
          // if (OORAuthCan) {
          //   set(
          //     produce((state: IMeetStore) => {
          //       const existingOORAuth =
          //         state.ceremonySteps.OORAuth.approvedCandidates.findIndex(
          //           (c) => c.id === OORAuthCan.id
          //         )
          //       if (existingOORAuth > -1) {
          //         state.ceremonySteps.OORAuth.approvedCandidates[existingOORAuth] =
          //           OORAuthCan
          //       } else
          //         state.ceremonySteps.OORAuth.approvedCandidates.push(OORAuthCan)
          //     })
          //   )
          // }

          const roleCredRecipients = extractRoleCredRecipients(message)
          if (roleCredRecipients) {
            set(
              produce((state: IMeetStore) => {
                state.ceremonySteps.OORAuth.roleCredentialRecipients =
                  roleCredRecipients
              })
            )
          }

          // const roleCredIssuer = extractRoleCredIssuer(message)
          // if (roleCredIssuer) {
          //   set(
          //     produce((state: IMeetStore) => {
          //       const index =
          //         state.ceremonySteps.OORAuth.approvedCandidates.findIndex(
          //           (c) => c.id === roleCredIssuer.issuerTo
          //         )
          //       if (index > -1) {
          //         let record = state.ceremonySteps.OORAuth.approvedCandidates[index]
          //         let issuerIndx = record.issuerMembers.findIndex(
          //           (c) => c.issuerTo === roleCredIssuer.issuerTo
          //         )

          //         if (issuerIndx > -1) {
          //           state.ceremonySteps.OORAuth.approvedCandidates[
          //             index
          //           ].issuerMembers[issuerIndx] = roleCredIssuer
          //         } else {
          //           state.ceremonySteps.OORAuth.approvedCandidates[
          //             index
          //           ].issuerMembers.push(roleCredIssuer)
          //         }
          //       }
          //     })
          //   )
          // }

          const MultisigMemberSignal = extractMultisigMemberSignal(message)
          if (MultisigMemberSignal) {
            set(
              produce((state: IMeetStore) => {
                state.ceremonySteps.multiSig.multisigMemberSignal =
                  MultisigMemberSignal
              })
            )
          }

          const credentialIssuanceStep = extractCredentialIssuanceStep(message)
          if (credentialIssuanceStep) {
            set(
              produce((state: IMeetStore) => {
                state.ceremonySteps.credentialIssuanceStep =
                  credentialIssuanceStep
              })
            )
          }

          const currentLARAuthCount = extractCurrentLARAuthCount(message)
          if (currentLARAuthCount) {
            set(
              produce((state: IMeetStore) => {
                state.ceremonySteps.currentLARAuthCount = currentLARAuthCount
              })
            )
          }

          const leadQAR = extractLeadQAR(message)
          if (leadQAR) {
            set(
              produce((state: IMeetStore) => {
                state.ceremonySteps.leadQAR = leadQAR.leadQAR
              })
            )
          }

          const secondaryQARApproval = extractSecondaryQARApproval(message)
          if (secondaryQARApproval) {
            set(
              produce((state: IMeetStore) => {
                state.ceremonySteps.OORAuth.recipients.forEach((recipient) => {
                  if (
                    !recipient.qarApproval.includes(
                      secondaryQARApproval.secondaryQAR
                    )
                  )
                    recipient.qarApproval.push(
                      secondaryQARApproval.secondaryQAR
                    )
                })
              })
            )
          }
        }
      )
    },

    setJitsiConnection: (connection) => {
      // t-6 / M3: connection held in the per-store closure box, mirroring setRoom
      // (symmetry — endCeremony reads both boxes in adjacent lines). The only
      // production writer is Meet.tsx:757.
      _connectionBox.current = connection
    },
    setConferenceStatus: (conferenceStatus) => {
      set(() => ({ conferenceStatus }))
    },
    addParticipants: (participant) => {
      set(
        produce((state: IMeetStore) => {
          const participantExist = !!state.ceremonySteps.participants.filter(
            (p) => p.id === participant.id
          ).length

          if (!participantExist)
            state.ceremonySteps.participants.push({
              ...participant,
              joinedMeetingAt: new Date().getTime()
            })
        })
      )
    },
    updateParticipant: (participant) => {
      set(
        produce((state: IMeetStore) => {
          const participantIndex = state.ceremonySteps.participants.findIndex(
            (p) => p.id === participant.id
          )

          // Guard against an unknown id: findIndex returns -1, and writing
          // `participants[-1] = {...}` would smuggle a non-index property onto
          // the array and corrupt its shape. An update for a participant that
          // is not present is a no-op — insertion is owned by addParticipants
          // (which de-dups by id). See BUG-0044.
          if (participantIndex === -1) return

          state.ceremonySteps.participants[participantIndex] = {
            ...state.ceremonySteps.participants[participantIndex],
            ...participant
          }
        })
      )
    },
    updateDevices: (availableDevices) => {
      const devices = groupDevicesByKind(availableDevices)
      set(() => ({ devices }))
    },
    removeParticipant: (id) => {
      set(
        produce((state: IMeetStore) => {
          const participantExist = state.ceremonySteps.participants.filter(
            (p) => p.id !== id
          )

          state.ceremonySteps.participants = participantExist
        })
      )
    },
    getMeetingDetails: async (meetingId) => {
      set(
        produce((state: IMeetStore) => {
          state.meetingDetails.fetchingStatus = FETCHING_STATUS.FETCHING
        })
      )
      try {
        const details = await getMeetingByID(meetingId)
        set(
          produce((state: IMeetStore) => {
            ;((state.meetingDetails.details = details),
              (state.meetingDetails.fetchingStatus = FETCHING_STATUS.COMPLETE))
          })
        )
      } catch (ex) {
        set(
          produce((state: IMeetStore) => {
            ;((state.meetingDetails.details = {} as GetMeetingResponseDto),
              (state.meetingDetails.fetchingStatus = FETCHING_STATUS.FAILED))
          })
        )
      }
    },
    setCurrentLARAuthCount: (currentLARAuthCount) => {
      set(
        produce((state: IMeetStore) => {
          state.ceremonySteps.currentLARAuthCount = currentLARAuthCount
        })
      )

      // t-6 step 3: read the per-store boxed room (closure var) instead of the
      // module global. Still throws TypeError before setRoom (box.current
      // undefined), preserving CER-3.
      _roomBox.current.sendMessage(
        prepareCurrentLARAuthCount(currentLARAuthCount)
      )
    },
    setQARCurrentStep: (step) => {
      set(
        produce((state: IMeetStore) => {
          state.ceremonySteps.qarCurrentStep = step
        })
      )

      _roomBox.current.sendMessage(prepareQARCeremonySteps(step))
    },
    sendMessage: (msg) => {
      let message = msg

      if (typeof msg !== 'string') {
        message = JSON.stringify(message)
      }

      _roomBox.current.sendMessage(message)
    },
    // DF-2387: optimistic local activation. The Activate onClick used to ONLY
    // broadcast prepareActiveLAR(...) over Jitsi; `activeLAR` was updated only
    // when that message round-tripped back through the MESSAGE_RECEIVED handler.
    // Until then getCTA stayed in its clickable branch (activeLAR !== id), so the
    // operator had to click repeatedly. Setting the optimistic state in the same
    // tick as the broadcast makes the UI respond on the FIRST click; the reflected
    // MESSAGE_RECEIVED assignment then writes the identical value (idempotent).
    activateLARLocally: (participantID, options) => {
      const { sendMessage } = get()
      set(
        produce((state: IMeetStore) => {
          state.ceremonySteps.activeLAR = participantID
        })
      )
      sendMessage(prepareActiveLAR(participantID))
      // OOR path preserves the second broadcast that resets activeOOR. Order and
      // both sends are kept identical to the original _activateLar.
      if (options?.isOOR) {
        sendMessage(prepareActiveOOR('00'))
      }
    },
    endCeremony: () => {
      const { setQARCurrentStep } = get()
      setQARCurrentStep(QAR_CEREMONY_STEP.END)
      _roomBox.current.leave()
      _connectionBox.current && _connectionBox.current.disconnect()
    },
    setLEMultisigInceptPayload: (
      payload: CreateLEMultisigAidMessagePayload
    ) => {
      set(
        produce((state: IMeetStore) => {
          state.ceremonySteps.multiSig.leMultisigAidInceptPayload = payload
        })
      )
    },
    logEvents: (eventMessages: string[]) => {
      set(
        produce((state: IMeetStore) => {
          state.ceremonySteps.eventLogs.push(...eventMessages)
        })
      )
    },
    setRecipient: (recipients) => {
      set(
        produce((state: IMeetStore) => {
          state.ceremonySteps.Recipients = recipients
        })
      )
    },
    initMeetSubscribers: (
      // Default-arg DI seam (Stage 3 / Stage 8α pattern). Production callers pass no
      // args and bind to the global `useStore` singleton. Tests can pass a
      // `createTestStore()`-derived `subscribe`/`getState`/`setState` triple to
      // drive this subscriber against an isolated store without touching the
      // global. See `src/state/__tests__/meet-seam.spec.ts` for the proof-of-seam.
      subscribe = useStore.subscribe,
      getState = useStore.getState,
      setState = useStore.setState
    ) => {
      // BUG-0037 / Stage 8.5 item 6 fix — strategy (d) frozen-when-stepCompleted.
      //
      // Once the AID-verification step is COMPLETE for every participant
      // observed so far, the participant set is frozen — a late-joining LAR
      // (or any new AIDTodos entry, including a WAITING one) MUST NOT be
      // silently absorbed into the ceremony, because the audio AID-verification
      // ritual will not be performed for that participant and the resulting
      // credential's signing group would include an unverified identity.
      //
      // This subscriber observes both the OLD and the NEW AIDTodos length on
      // each change. When `stepCompleted === true` AND new entries appeared
      // since the last fire, we REJECT the addition by reverting AIDTodos to
      // the prior snapshot AND emit a console.error so the operator / UI
      // sees that a late joiner was refused.
      //
      // No "reset" path exists from `stepCompleted: true` back to false except
      // via `resetMeetStore` / `endCeremony` — the freeze is intentional.
      let previousAIDTodos: any[] = []
      const unsubscribe = (subscribe as any)(
        (state: IMeetStore) => state.ceremonySteps.AIDVerification.AIDTodos,
        (AIDTodos: any) => {
          const state = getState()
          const stepCompleted =
            state.ceremonySteps?.AIDVerification?.stepCompleted === true

          // FREEZE GUARD (BUG-0037 strategy d): if the step is already
          // complete AND a NEW entry appeared, revert. We compare set-of-`to`
          // because the AIDTodos array may be re-sorted or have existing
          // entries mutated (e.g., a from[].completionStatus flip from
          // AID_VERIFICATION_I_Heard_It) without representing a "new joiner".
          if (stepCompleted) {
            const prevTos = new Set(previousAIDTodos.map((t: any) => t.to))
            const hasNewParticipant = (AIDTodos as any[]).some(
              (t: any) => !prevTos.has(t.to)
            )
            if (hasNewParticipant) {
              console.error(
                'BUG-0037 freeze: late LAR rejected — AID-verification step is COMPLETE; new participant cannot be added. ' +
                  'Refusing to silently absorb late joiner into the ceremony signing group.'
              )
              setState({
                ceremonySteps: {
                  ...state.ceremonySteps,
                  AIDVerification: {
                    ...state.ceremonySteps.AIDVerification,
                    AIDTodos: previousAIDTodos
                  }
                }
              } as any)
              // previousAIDTodos remains the frozen snapshot; do NOT update it.
              return
            }
          }

          previousAIDTodos = AIDTodos as any[]

          state.checkAndActivateLARForAID(AIDTodos)

          const allChallengesComplete =
            AIDTodos.length > 0 &&
            !AIDTodos.some(
              (lc: any) => lc.triggerSignify !== TriggerSignify.COMPLETE
            )

          if (allChallengesComplete) {
            setState({
              ceremonySteps: {
                ...state.ceremonySteps,
                AIDVerification: {
                  ...state.ceremonySteps.AIDVerification,
                  stepCompleted: true
                }
              }
            } as any)
          }
        }
      )

      return unsubscribe
    },
    generateLARsVerificationsForAID: () => {
      const {
        sendMessage,
        authentication,
        ceremonySteps: { participants, Recipients }
      } = get()
      const ceremonyType = ceremonyTypeFromURL()

      let allParticipants: Participant[]
      let fromParticipants: Participant[]

      switch (ceremonyType) {
        case CEREMONY_TYPE.ECR:
        case CEREMONY_TYPE.OOR:
          const onlyRecipientParticipants = participants.filter((p) =>
            isRecipient(p.id, Recipients)
          )

          fromParticipants = participants.filter((p) => isLAR(p.role))
          allParticipants = onlyRecipientParticipants
          break
        default:
          const lars = participants.filter((p) => isLAR(p.role))

          fromParticipants = lars
          allParticipants = lars
          break
      }

      const aidStrings: AIDVerificationString[] = []

      allParticipants.forEach((part) => {
        const entryParticipant: AIDVerificationString = {
          from: [],
          to: part.id,
          triggerSignify: TriggerSignify.WAITING,
          completed: false,
          messageId: crypto.randomUUID(),
          // Participant.joinedMeetingAt is optional (set when the participant
          // joins Jitsi); the AIDVerificationString contract requires a number.
          // Fall back to 0 (epoch) when the timestamp is missing — sorts last,
          // and downstream consumers treat 0 as "unknown join time".
          joinedMeetingAt: part.joinedMeetingAt ?? 0
        }

        aidStrings.push(entryParticipant)
        const allParticipantsExceptTheCurrentOne = fromParticipants.filter(
          (participant) => participant.id !== part.id
        )

        // populate list of listeners
        allParticipantsExceptTheCurrentOne.forEach((fromParticipant) => {
          entryParticipant.from.push({
            participantId: fromParticipant.id,
            completionStatus: false
          })
        })

        // add another aid string for the lead QAR ( who's not present in the participants list )
        // authentication.userInfo is nullable until session hydration; this
        // code runs inside a ceremony where userInfo is always populated, but
        // the type system can't follow that flow — coalesce defensively.
        entryParticipant.from.push({
          // TECH_DEBT: improve_lead_qar_lookup
          participantId: authentication.userInfo?.id ?? '',
          completionStatus: false
        })
      })

      sendMessage(
        new JitsiMessageCreator(
          JitsiMessagesEnum.AID_VERIFICATION_GENERATE_LARS_TODOS,
          aidStrings
        )
      )
    },
    iSaidItForAID: (todo) => {
      const generalJitsiPayload: AIDVerificationString = {
        ...todo,
        triggerSignify: TriggerSignify.ACCEPT
      }

      get().sendMessage(
        new JitsiMessageCreator(
          JitsiMessagesEnum.AID_VERIFICATION_I_Said_It,
          generalJitsiPayload
        )
      )
    },
    iHeardItForAID: (todo) => {
      const { authentication, sendMessage } = get()
      const updatedTodo = cloneDeep(todo)
      const userFromEntry = updatedTodo.from.find(
        // authentication.userInfo is nullable; if missing we treat the lookup
        // as "no matching entry", which the BUG-0041 guard below converts into
        // a refuse-to-forward (defensive).
        (from) => from.participantId === authentication.userInfo?.id
      )

      // BUG-0041 / Stage 8.5 item 7 — vacuous-truth + missing-entry guard.
      // Security Hawk C2 (sibling of BUG-0035 / CER-1 at meet.ts:1057-1061):
      // a peer participant supplying a crafted Jitsi message with `from: []`
      // (or with `from` that excludes the current user) would otherwise reach
      // `[].every(...) === true` (vacuous truth) and flip `triggerSignify` to
      // COMPLETE — forging the AID-verification ritual for a participant
      // who is part of the trust model ONLY AFTER AID verification.
      //
      // Refuse to mutate / forward the message when either invariant fails:
      //   (a) `updatedTodo.from` is empty (vacuous-truth path), or
      //   (b) the current user's entry isn't in `from` (the message wasn't
      //       addressed to us — pre-fix this caused a TypeError on the
      //       implicit `userFromEntry.completionStatus = true`).
      if (!userFromEntry || updatedTodo.from.length === 0) {
        console.error(
          'BUG-0041 guard: iHeardItForAID received malformed todo (empty `from` or current user not addressed); refusing to forward.'
        )
        return
      }
      userFromEntry.completionStatus = true

      const didAllParticipantsHeardItRight =
        updatedTodo.from.length > 0 &&
        updatedTodo.from.every((from) => from.completionStatus === true)

      updatedTodo.triggerSignify = didAllParticipantsHeardItRight
        ? TriggerSignify.COMPLETE
        : updatedTodo.triggerSignify

      updatedTodo.completed = true

      sendMessage(
        new JitsiMessageCreator(
          JitsiMessagesEnum.AID_VERIFICATION_I_Heard_It,
          updatedTodo
        )
      )
    },
    checkAndActivateLARForAID: (todos) => {
      const {
        ceremonySteps: { activeLAR },
        sendMessage
      } = get()
      const currentUserChallenges = todos.filter((c) => c.to === activeLAR)

      // BUG-0035 / CER-1 fix (Stage 8 S8-2): guard against vacuous-truth on an
      // empty filtered subset. Without the `length > 0` guard, when
      // `activeLAR === null` and no todo has `to === null`,
      // `currentUserChallenges` is `[]` and `[].every(...)` is `true`, which
      // fires `sendMessage(prepareActiveLAR(...))` and activates a LAR before
      // any was intentionally chosen. The guard target is the LAR-filtered
      // subset (not the unfiltered `todos`) — the analogous guard already
      // exists in `initMeetSubscribers` at meet.ts:914-916 against the
      // unfiltered `AIDTodos`. See bug-log.md BUG-0035.
      const allChallengesComplete =
        currentUserChallenges.length > 0 &&
        currentUserChallenges.every(
          (lc) => lc.triggerSignify === TriggerSignify.COMPLETE
        )

      const waitingChallenge = todos.find(
        (lc) => lc.triggerSignify === TriggerSignify.WAITING
      )?.to
      const failedChallenge = todos.find(
        (lc) => lc.triggerSignify === TriggerSignify.FAILED
      )?.to

      if (allChallengesComplete) {
        sendMessage(prepareActiveLAR(waitingChallenge || '00')) // Defaults to '00' if no waiting challenge
      } else {
        // Check if any challenge has failed and update accordingly
        const hasFailedChallenge = currentUserChallenges.some(
          (lc) => lc.triggerSignify === TriggerSignify.FAILED
        )
        if (hasFailedChallenge && failedChallenge) {
          sendMessage(prepareActiveLAR(failedChallenge))
        }
      }

      if (!activeLAR || activeLAR === '00') {
        sendMessage(
          prepareActiveLAR(
            failedChallenge || waitingChallenge || activeLAR || '00'
          )
        )
      }
    },
    sendErrorMessageToAllParticipantsAndNotifySentry: ({
      sentryContext,
      sentryContextPayload,
      message,
      error
    }) => {
      const { sendMessage } = get()
      captureErrorWithScope((scope) => {
        scope.setContext(sentryContext, { ...sentryContextPayload, message })
      }, error)
      sendMessage(
        new JitsiMessageCreator(JitsiMessagesEnum.LEvLEI_ISSUANCE_ERROR, {
          type: sentryContext,
          message: message ?? ''
        })
      )
    },
    resetMeetStore: () => {
      set(initialState)
      // CER-3 / t-6: clear the per-store boxed room+connection VIA the store
      // action (this is a REAL reset, not a no-op). `set(initialState)` cannot
      // do it because the stable box is intentionally never carried through
      // `set` (B1); the box identity is preserved (so devtools/subscriber safety
      // holds) and only `.current` is cleared.
      _roomBox.current = undefined
      _connectionBox.current = undefined
    }
  }
}

export default meetStore

/**
 * Test-only seam (Stage 8α), back-compat export.
 *
 * t-6 (cd-0021 / DF-2933): the room/connection are no longer module-level
 * globals — they live in per-store closure boxes (see `RoomBox`). The REAL
 * reset therefore happens VIA the store action `resetMeetStore`, which clears
 * `_roomBox.current` / `_connectionBox.current` for THAT store. A module-level
 * function cannot reach a particular store's closure box, so this seam can no
 * longer reset the room itself.
 *
 * It is kept (a no-op) only because `helpers.ts:resetAllModuleSingletons` calls
 * `_resetConferenceRoomForTest?.()`. That helper ALSO calls
 * `store.resetAllStores()` → `resetMeetStore()` when a store is passed, which is
 * what now performs the genuine per-store box reset. Specs that build a FRESH
 * `createTestStore()` (e.g. the CER-3 spec) get a fresh box with
 * `current: undefined` automatically, so no cross-test room leak is possible —
 * exactly the determinism this seam used to provide for the module global.
 *
 * Underscore prefix marks it as not-for-production use. No production code
 * should import this.
 */
export function _resetConferenceRoomForTest(): void {
  // Intentionally empty: the per-store box reset lives in `resetMeetStore`
  // (reached via `store.resetAllStores()` in `resetAllModuleSingletons`), and a
  // fresh `createTestStore()` starts with an empty box. There is no module-level
  // room state left to clear.
}
